import React, { useMemo, useCallback } from 'react';
import { camelCase } from 'lodash';
import { FieldProps, useFieldProps } from '../../../document/pages/Assistance/customizable/Field';
import { useAssistanceFieldContext } from '../../../document/pages/Assistance/AssistanceFieldContext';
import { useAssistanceContext } from '../../../document/pages/Assistance/AssistanceContext';
import AutoCompleteField from '../../../core/components/Fields/AutoCompleteField';
import { renderArticleIdOption } from '../../../document/components/useLookup';
import useForceSelectionLookupProps, {
    useForceSelectionReselectEventListener,
} from '../../../document/components/useForceSelectionLookupProps';

export const JAEGER_FIELD_NAMES = {
    NORM: 'norm',
    OBERFLAECHE: 'oberflaeche',
    GUETE: 'guete',
    MASS: 'mass',
} as const;

type JaegerFieldName = (typeof JAEGER_FIELD_NAMES)[keyof typeof JAEGER_FIELD_NAMES];

interface JaegerMaterialSpecificationFieldProps extends FieldProps {
    targetFieldName: JaegerFieldName;
}

/**
 * Custom field component for Jaeger's material specification fields.
 * The four fields (norm, oberflaeche, guete, mass) all use a combined search term
 * consisting of all four field values: "{norm} {guete} {oberflaeche} {mass}"
 *
 * When the user types in any of these fields, the search term includes:
 * - The current input value for the target field
 * - The stored values for the other three fields
 */
export const JaegerMaterialSpecificationField = (props: JaegerMaterialSpecificationFieldProps) => {
    const { targetFieldName } = props;
    const { clientPartitionId, config, itemIndex, itemType } = useAssistanceFieldContext();
    const { document } = useAssistanceContext();
    const { lookupDefinitionId, lookupFieldName, allowInvalidIds } = config;

    const fieldProps = useFieldProps(props);

    // Get all four field values from the same line item
    const { norm, oberflaeche, guete, mass } = useMemo(() => {
        if (itemIndex == null || !itemType) {
            return { norm: '', oberflaeche: '', guete: '', mass: '' };
        }

        // Use camelCase for document access (line_items -> lineItems)
        const items = document?.[camelCase(itemType)];
        const currentItem = items?.[itemIndex];

        if (!currentItem) {
            return { norm: '', oberflaeche: '', guete: '', mass: '' };
        }

        return {
            norm: currentItem[JAEGER_FIELD_NAMES.NORM]?.value || '',
            oberflaeche: currentItem[JAEGER_FIELD_NAMES.OBERFLAECHE]?.value || '',
            guete: currentItem[JAEGER_FIELD_NAMES.GUETE]?.value || '',
            mass: currentItem[JAEGER_FIELD_NAMES.MASS]?.value || '',
        };
    }, [document, itemIndex, itemType]);

    // Create search term transformer that combines all four field values
    const transformSearchTerm = useCallback(
        (searchTerm: string) => {
            // Construct combined search term using all four field values
            // For the target field, use the current search term (user's input)
            // For other fields, use their stored values
            const values = {
                [JAEGER_FIELD_NAMES.NORM]: targetFieldName === JAEGER_FIELD_NAMES.NORM ? searchTerm : norm,
                [JAEGER_FIELD_NAMES.GUETE]: targetFieldName === JAEGER_FIELD_NAMES.GUETE ? searchTerm : guete,
                [JAEGER_FIELD_NAMES.OBERFLAECHE]:
                    targetFieldName === JAEGER_FIELD_NAMES.OBERFLAECHE ? searchTerm : oberflaeche,
                [JAEGER_FIELD_NAMES.MASS]: targetFieldName === JAEGER_FIELD_NAMES.MASS ? searchTerm : mass,
            };

            // Combine in the specified order: norm, guete, oberflaeche, mass
            return [
                values[JAEGER_FIELD_NAMES.NORM],
                values[JAEGER_FIELD_NAMES.GUETE],
                values[JAEGER_FIELD_NAMES.OBERFLAECHE],
                values[JAEGER_FIELD_NAMES.MASS],
            ]
                .filter((v) => v && v.trim())
                .join(' ');
        },
        [norm, oberflaeche, guete, mass, targetFieldName]
    );

    // Use the standard useForceSelectionLookupProps hook with search term transformer
    const forceSelectionLookupProps = useForceSelectionLookupProps({
        lookupType: 'ArticleLookupType',
        lookupDefinitionId,
        lookupFieldName,
        partitionId: clientPartitionId,
        allowInvalidIds,
        ...fieldProps,
        onUpdatePayloadChange: props.onUpdatePayloadChange,
        isPartitionIdField: config?.isPartitionIdField === true,
        // Only apply transform for non-NORM fields (NORM searches by itself only)
        transformSearchTerm: targetFieldName === JAEGER_FIELD_NAMES.NORM ? undefined : transformSearchTerm,
    });

    useForceSelectionReselectEventListener(props.inputRef, forceSelectionLookupProps.onValueChange);

    return (
        <AutoCompleteField
            {...fieldProps}
            {...forceSelectionLookupProps}
            renderOption={renderArticleIdOption(lookupFieldName)}
        />
    );
};
